import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trophy, Eye, EyeSlash } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const HERO = "/pocket-aces.png";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (mode === "register") {
      if (!phone.trim()) {
        setError("A phone number is required.");
        return;
      }
      if (password !== confirmPassword) {
        setError("Passwords do not match.");
        return;
      }
    }
    setLoading(true);
    try {
      if (mode === "login") {
        await login(identifier.trim(), password);
      } else {
        await register({ name: name.trim(), email: email.trim() || null, phone: phone.trim(), password });
      }
      navigate("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row grain relative">
      {/* Hero */}
      <div className="relative md:w-1/2 h-48 md:h-auto overflow-hidden">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/70" />
        <div className="relative z-10 h-full flex flex-col justify-end p-8">
          <div className="flex items-center gap-2 mb-2">
            <Trophy size={32} weight="fill" className="text-primary" />
            <span className="font-heading font-black text-3xl tracking-tighter uppercase">Vegas 2027</span>
          </div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl uppercase tracking-tighter leading-none">
            Live poker<br />leaderboard
          </h1>
          <p className="text-muted-foreground mt-3 max-w-sm hidden md:block">
            Track placements, entries and standings across every game — live, on any device.
          </p>
        </div>
      </div>

      {/* Form */}
      <div className="flex-1 flex items-center justify-center p-6 relative z-10">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="auth-form">
          <div className="flex gap-2 mb-8 border border-border rounded-sm p-1 bg-card">
            {["login", "register"].map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => { setMode(m); setError(""); }}
                data-testid={`tab-${m}`}
                className={`flex-1 py-2 rounded-sm font-heading font-bold uppercase tracking-wide text-sm transition-colors ${
                  mode === m ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-white"
                }`}
              >
                {m === "login" ? "Sign In" : "Create Account"}
              </button>
            ))}
          </div>

          {mode === "register" && (
            <div className="space-y-4 mb-4">
              <div>
                <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Name</Label>
                <Input data-testid="reg-name" value={name} onChange={(e) => setName(e.target.value)} required placeholder="Your name" className="mt-1.5 bg-card border-border h-12 text-base" />
              </div>
              <div>
                <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Phone (AUS) *</Label>
                <Input data-testid="reg-phone" value={phone} onChange={(e) => setPhone(e.target.value)} required placeholder="0400 000 000" className="mt-1.5 bg-card border-border h-12 text-base" />
              </div>
              <div>
                <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Email (optional)</Label>
                <Input data-testid="reg-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" className="mt-1.5 bg-card border-border h-12 text-base" />
              </div>
            </div>
          )}

          {mode === "login" && (
            <div className="mb-4">
              <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Email or Phone</Label>
              <Input data-testid="login-identifier" value={identifier} onChange={(e) => setIdentifier(e.target.value)} required placeholder="Email or phone number" className="mt-1.5 bg-card border-border h-12 text-base" />
            </div>
          )}

          <div className="mb-5">
            <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Password</Label>
            <div className="relative mt-1.5">
              <Input data-testid="password-input" type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="••••••••" className="bg-card border-border h-12 text-base pr-12" />
              <button type="button" onClick={() => setShowPassword((v) => !v)} data-testid="toggle-password" aria-label={showPassword ? "Hide password" : "Show password"} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white transition-colors">
                {showPassword ? <EyeSlash size={20} weight="bold" /> : <Eye size={20} weight="bold" />}
              </button>
            </div>
          </div>

          {mode === "register" && (
            <div className="mb-6">
              <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Re-enter Password</Label>
              <div className="relative mt-1.5">
                <Input data-testid="confirm-password-input" type={showConfirm ? "text" : "password"} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required placeholder="••••••••" className="bg-card border-border h-12 text-base pr-12" />
                <button type="button" onClick={() => setShowConfirm((v) => !v)} data-testid="toggle-confirm-password" aria-label={showConfirm ? "Hide password" : "Show password"} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white transition-colors">
                  {showConfirm ? <EyeSlash size={20} weight="bold" /> : <Eye size={20} weight="bold" />}
                </button>
              </div>
            </div>
          )}

          {mode === "login" && <div className="mb-1" />}

          {error && (
            <p data-testid="auth-error" className="text-kinetic text-sm mb-4 font-medium">{error}</p>
          )}

          <Button
            type="submit"
            disabled={loading}
            data-testid="auth-submit"
            className="w-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground font-heading font-bold uppercase tracking-widest text-base rounded-sm"
          >
            {loading ? "Please wait…" : mode === "login" ? "Sign In" : "Create Account"}
          </Button>
        </form>
      </div>
    </div>
  );
}
